import { defineStore } from 'pinia';
import axios from 'axios';
import { ref, watch, computed } from 'vue';
import useNotifications from '@/store/notification';
import { debounce } from 'lodash'; // Added for debouncing
import { useLogger } from '@/composables/useLogger';

const logger = useLogger('equipments');

// ---------------- NORMALIZE ADDRESS (matches AddressSerializer) ----------------
const emptyAddress = {
  id: null,
  street_address: '',
  street_address2: '',
  city: '',
  state: '',
  zip_code: '',
  country: '',
  address_type: null,
  is_default: false,
  latitude: null,
  longitude: null,
  google_place_id: '',
  formatted_address: '',
  raw_geocode: null,
  full_address: '',
  has_coordinates: false,
};

const normaliseAddress = (address) => {
  if (!address) return { ...emptyAddress };

  const street_address = address.street_address ?? '';
  const street_address2 = address.street_address2 ?? '';
  const city = address.city ?? '';
  const state = address.state ?? '';
  const zip_code = address.zip_code ?? '';
  const country = address.country ?? '';

  const latitude = address.latitude ?? null;
  const longitude = address.longitude ?? null;

  const full_address =
    address.full_address ??
    [street_address, street_address2, city, state, zip_code, country]
      .filter(Boolean)
      .join(', ');

  const has_coordinates =
    address.has_coordinates ??
    (latitude !== null && longitude !== null);

  return {
    id: address.id ?? null,
    street_address,
    street_address2,
    city,
    state,
    zip_code,
    country,
    address_type: address.address_type ?? null,
    is_default: address.is_default ?? false,
    latitude,
    longitude,
    google_place_id: address.google_place_id ?? '',
    formatted_address: address.formatted_address ?? full_address,
    raw_geocode: address.raw_geocode ?? null,
    full_address,
    has_coordinates,
  };
};

// Wrap equipment so it always has equipment.address normalized
const normaliseEquipment = (item) => {
  if (!item) return item;
  return {
    ...item,
    address: normaliseAddress(item.address),
  };
};

export const useEquipmentsStore = defineStore('equipmentStore', () => {
  const api_base_url = import.meta.env.VITE_API_BASE_URL || window.location.origin;
  const { showNotification } = useNotifications();

  // State
  const equipments = ref([]);
  const categories = ref([]);
  const categoriesTotal = ref(0);
  const categoriesHasMore = ref(false);
  const cities = ref([]);
  const citiesTotal = ref(0);
  const citiesHasMore = ref(false);
  const selectedEquipment = ref(null);
  const userEquipments = ref([]);
  const userEditableEquipmentsIds = ref([]);
  const selectedCategory = ref('All');
  const urlCategory = ref(''); // from ?cat=slug

  const selectedCategories = ref([]);
  const selectedCities = ref([]);
  const selectedState = ref('');
  const selectedCountry = ref('');
  // Granular loading flags
  const isLoadingEquipments = ref(false);
  const isLoadingCategories = ref(false);
  const isLoadingCities = ref(false);
  // True once ANY equipment fetch (plain or filtered) has completed at least once
  const hasCompletedEquipmentFetch = ref(false);
  // Combined flag (true if anything is loading) — kept for backward compat
  const isLoading = computed(() => isLoadingEquipments.value || isLoadingCategories.value || isLoadingCities.value);
  const error = ref(null);
  const searchQuery = ref('');
  const filteredEquipments = ref([]);
  const nextPageUrl = ref(null);
  const previousPageUrl = ref(null);
  const totalPages = ref(1);
  const currentPage = ref(1);
  const totalItems = ref(0);
  const pageLinks = ref([]);
  const pageSize = ref(8);
  const relatedEquipments = ref([]);

  const categoryMap = computed(() =>
    categories.value.reduce((map, c) => {
      map[c.slug] = c.id;
      return map;
    }, {})
  );

  // Cache state
  const cache = ref({
    equipments: {},
    categories: null,
    relatedEquipments: {},
    selectedEquipment: {},
    timestamp: {},
  });

  // Track ongoing requests
  const pendingRequests = ref({});

  // Cache TTL (5 minutes)
  const CACHE_TTL = 5 * 60 * 1000;

  // Utility Functions
  const isCacheValid = (key) => {
    const timestamp = cache.value.timestamp[key];
    return timestamp && Date.now() - timestamp < CACHE_TTL;
  };

  const truncateText = (text, length) =>
    text.length > length ? `${text.slice(0, length)}...` : text;

  const deduplicateEquipments = (items) => {
    const seen = new Set();
    return items.filter((item) => {
      if (!item?.id) return false;
      if (seen.has(item.id)) return false;
      seen.add(item.id);
      return true;
    });
  };

  // Generic function to handle single requests
  const withSingleRequest = async (key, fn) => {
    // Return cached Promise if request is pending
    if (pendingRequests.value[key]) {
      return pendingRequests.value[key];
    }

    // Execute the request and store the Promise
    const promise = fn();
    pendingRequests.value[key] = promise;

    try {
      const result = await promise;
      return result;
    } finally {
      // Clear the pending request
      delete pendingRequests.value[key];
    }
  };

  // Actions
  const fetchRelatedEquipments = async (equipmentId) => {
    const cacheKey = `related_${equipmentId}`;
    if (cache.value.relatedEquipments[equipmentId] && isCacheValid(cacheKey)) {
      relatedEquipments.value = cache.value.relatedEquipments[equipmentId];
      return;
    }

    return withSingleRequest(`related_${equipmentId}`, async () => {
      isLoadingEquipments.value = true;
      try {
        const response = await axios.get(
          `${api_base_url}/api/equipments/${equipmentId}/related/`
        );
        const raw = deduplicateEquipments(response.data || []);
        const data = raw.map(normaliseEquipment);
        relatedEquipments.value = data;
        cache.value.relatedEquipments[equipmentId] = data;
        cache.value.timestamp[cacheKey] = Date.now();
      } catch (error) {
        logger.error('Error fetching related equipment', error);
        showNotification('Error', 'Failed to fetch related equipments.', 'error');
      } finally {
        isLoadingEquipments.value = false;
      }
    });
  };

  const fetchEquipments = async (
    url = `${api_base_url}/api/equipments/?page_size=${pageSize.value}`
  ) => {
    const cacheKey = `equipments_${url}`;
    if (cache.value.equipments[url] && isCacheValid(cacheKey)) {
      const cached = cache.value.equipments[url];
      equipments.value = cached.data;
      filteredEquipments.value = cached.data;
      nextPageUrl.value = cached.next;
      previousPageUrl.value = cached.previous;
      pageLinks.value = cached.page_links;
      totalPages.value = cached.total_pages;
      currentPage.value = cached.current_page;
      totalItems.value = cached.count;
      hasCompletedEquipmentFetch.value = true;
      return;
    }

    return withSingleRequest(`equipments_${url}`, async () => {
      isLoadingEquipments.value = true;
      error.value = null;

      try {
        const response = await axios.get(url, { withCredentials: true });
        const raw = deduplicateEquipments(response.data?.results || []);
        const data = raw.map(normaliseEquipment);

        // Rewrite host in backend-returned URLs to match our configured api_base_url
        // (backend may return localhost while we use 127.0.0.1, or http while prod uses https)
        const normalisePageUrl = (backendUrl) => {
          if (!backendUrl) return null;
          try {
            const base = new URL(api_base_url);
            const u = new URL(backendUrl);
            u.protocol = base.protocol;
            u.host = base.host;
            return u.toString();
          } catch (e) {
            return backendUrl;
          }
        };

        equipments.value = data;
        filteredEquipments.value = data;
        nextPageUrl.value = normalisePageUrl(response.data?.next);
        previousPageUrl.value = normalisePageUrl(response.data?.previous);
        pageLinks.value = (response.data?.page_links || []).map((link) => ({
          ...link,
          url: normalisePageUrl(link.url),
        }));
        totalPages.value = response.data?.total_pages ?? 1;
        currentPage.value = response.data?.current_page ?? 1;
        totalItems.value = response.data?.count ?? 0;

        cache.value.equipments[url] = {
          data,
          next: nextPageUrl.value,
          previous: previousPageUrl.value,
          page_links: pageLinks.value,
          total_pages: totalPages.value,
          current_page: currentPage.value,
          count: totalItems.value,
        };
        cache.value.timestamp[cacheKey] = Date.now();
      } catch (err) {
        error.value = 'Failed to fetch equipments.';
        showNotification(
          'Error',
          `Fetching equipments failed: ${err.response?.data || err.message}`,
          'error'
        );
      } finally {
        isLoadingEquipments.value = false;
        hasCompletedEquipmentFetch.value = true;
      }
    });
  };

  const fetchFilteredEquipments = async ({
    category = '',
    search = '',
    categories = [],
    cities = [],
    state = '',
    country = '',
  } = {}) => {
    const cacheKey = JSON.stringify({ category, search, categories, cities, state, country, pageSize: pageSize.value });
    if (cache.value.equipments[cacheKey] && isCacheValid(`filtered_${cacheKey}`)) {
      const cached = cache.value.equipments[cacheKey];
      equipments.value = cached.data;
      filteredEquipments.value = cached.data;
      nextPageUrl.value = cached.next;
      previousPageUrl.value = cached.previous;
      pageLinks.value = cached.page_links;
      totalPages.value = cached.total_pages;
      currentPage.value = cached.current_page;
      totalItems.value = cached.count;
      hasCompletedEquipmentFetch.value = true;
      return;
    }

    return withSingleRequest(`filtered_${cacheKey}`, async () => {
      isLoadingEquipments.value = true;
      error.value = null;

      try {
        const params = new URLSearchParams();
        if (category) params.append('category', category);
        if (search.trim()) params.append('search', search.trim());
        if (categories.length > 0)
          params.append('categories', categories.join(','));
        if (cities.length > 0) params.append('cities', cities.join(','));
        if (state) params.append('state', state);
        if (country) params.append('country', country);
        params.append('page_size', pageSize.value);

        const normalisePageUrl = (backendUrl) => {
          if (!backendUrl) return null;
          try {
            const base = new URL(api_base_url);
            const u = new URL(backendUrl);
            u.protocol = base.protocol;
            u.host = base.host;
            return u.toString();
          } catch (e) {
            return backendUrl;
          }
        };

        const url = `${api_base_url}/api/equipments/filter/?${params.toString()}`;
        const response = await axios.get(url, { withCredentials: true });
        const raw = deduplicateEquipments(response.data?.results || []);
        const data = raw.map(normaliseEquipment);

        equipments.value = data;
        filteredEquipments.value = data;
        nextPageUrl.value = normalisePageUrl(response.data?.next);
        previousPageUrl.value = normalisePageUrl(response.data?.previous);
        pageLinks.value = (response.data?.page_links || []).map((link) => ({
          ...link,
          url: normalisePageUrl(link.url),
        }));
        totalPages.value = response.data?.total_pages ?? 1;
        currentPage.value = response.data?.current_page ?? 1;
        totalItems.value = response.data?.count ?? 0;

        cache.value.equipments[cacheKey] = {
          data,
          next: nextPageUrl.value,
          previous: previousPageUrl.value,
          page_links: pageLinks.value,
          total_pages: totalPages.value,
          current_page: currentPage.value,
          count: totalItems.value,
        };
        cache.value.timestamp[`filtered_${cacheKey}`] = Date.now();
      } catch (err) {
        showNotification(
          'Error',
          `Fetching filtered equipments failed: ${err.response?.data || err.message}`,
          'error'
        );
      } finally {
        isLoadingEquipments.value = false;
        hasCompletedEquipmentFetch.value = true;
      }
    });
  };

  const fetchUserEquipments = async () => {
    const cacheKey = 'user_equipments';
    if (cache.value.equipments[cacheKey] && isCacheValid(cacheKey)) {
      userEquipments.value = cache.value.equipments[cacheKey].data;
      return;
    }

    return withSingleRequest(cacheKey, async () => {
      isLoadingEquipments.value = true;
      error.value = null;

      try {
        const response = await axios.get(
          `${api_base_url}/api/user-equipment/`,
          { withCredentials: true }
        );
        const raw = deduplicateEquipments(response.data || []);
        const data = raw.map(normaliseEquipment);
        userEquipments.value = data;
        cache.value.equipments[cacheKey] = { data };
        cache.value.timestamp[cacheKey] = Date.now();
      } catch (err) {
        error.value = 'Failed to fetch user equipments.';
        showNotification(
          'Error',
          `Fetching user equipments failed: ${err.response?.data || err.message}`,
          'error'
        );
      } finally {
        isLoadingEquipments.value = false;
      }
    });
  };

  const fetchUserEditableEquipments = async () => {
    const cacheKey = 'user_editable_equipments';
    if (cache.value.equipments[cacheKey] && isCacheValid(cacheKey)) {
      userEditableEquipmentsIds.value = cache.value.equipments[cacheKey].data;
      return;
    }

    return withSingleRequest(cacheKey, async () => {
      isLoadingEquipments.value = true;
      error.value = null;

      try {
        const response = await axios.get(
          `${api_base_url}/api/user-editable-equipment/`,
          { withCredentials: true }
        );
        const data = response.data || [];
        userEditableEquipmentsIds.value = data;
        cache.value.equipments[cacheKey] = { data };
        cache.value.timestamp[cacheKey] = Date.now();
      } catch (err) {
        error.value = 'Failed to fetch editable equipments.';
        showNotification(
          'Error',
          `Fetching editable equipments failed: ${err.response?.data || err.message}`,
          'error'
        );
      } finally {
        isLoadingEquipments.value = false;
      }
    });
  };

  const getEquipmentById = async (id) => {
    const cacheKey = `equipment_${id}`;
    if (cache.value.selectedEquipment[id] && isCacheValid(cacheKey)) {
      selectedEquipment.value = cache.value.selectedEquipment[id];
      return;
    }

    const equipment = equipments.value.find(
      (item) => item.id === Number(id)
    );
    if (equipment) {
      selectedEquipment.value = equipment;
      cache.value.selectedEquipment[id] = equipment;
      cache.value.timestamp[cacheKey] = Date.now();
      return;
    }

    return withSingleRequest(cacheKey, async () => {
      isLoadingEquipments.value = true;
      error.value = null;

      try {
        const response = await axios.get(
          `${api_base_url}/api/equipments/${id}/`,
          { withCredentials: true }
        );
        const normalised = normaliseEquipment(response.data || null);
        selectedEquipment.value = normalised;
        if (normalised) {
          cache.value.selectedEquipment[id] = normalised;
          cache.value.timestamp[cacheKey] = Date.now();
        }
      } catch (err) {
        error.value = `Failed to fetch equipment with ID ${id}.`;
        showNotification(
          'Error',
          `Fetching equipment failed: ${err.response?.data || err.message}`,
          'error'
        );
      } finally {
        isLoadingEquipments.value = false;
      }
    });
  };

  const BATCH_SIZE = 30;

  const fetchCategories = async (append = false) => {
    const offset = append ? categories.value.length : 0;
    const cacheKey = `categories_${offset}`;

    if (!append && categories.value.length > 0) {
      return categories.value;
    }

    isLoadingCategories.value = true;
    error.value = null;

    try {
      const response = await axios.get(
        `${api_base_url}/api/categories/`,
        {
          params: { limit: BATCH_SIZE, offset },
          withCredentials: true,
        }
      );

      const data = response.data;
      const results = data.results || data || [];

      if (append) {
        categories.value = [...categories.value, ...results];
      } else {
        categories.value = results;
      }

      categoriesTotal.value = data.total || results.length;
      categoriesHasMore.value = data.has_more || false;
      cache.value.categories = categories.value;
      cache.value.timestamp['categories'] = Date.now();

      return categories.value;
    } catch (err) {
      error.value = err.response?.data || err.message;
      showNotification('Error', `Fetching categories failed: ${error.value}`, 'error');
      return [];
    } finally {
      isLoadingCategories.value = false;
    }
  };

  const searchCategories = async (query) => {
    isLoadingCategories.value = true;
    error.value = null;

    try {
      const response = await axios.get(
        `${api_base_url}/api/categories/`,
        {
          params: { search: query, limit: BATCH_SIZE, offset: 0 },
          withCredentials: true,
        }
      );

      const data = response.data;
      return {
        results: data.results || data || [],
        total: data.total || 0,
        has_more: data.has_more || false,
      };
    } catch (err) {
      error.value = err.response?.data || err.message;
      return { results: [], total: 0, has_more: false };
    } finally {
      isLoadingCategories.value = false;
    }
  };

  const fetchCities = async (append = false, search = '') => {
    const offset = append ? cities.value.length : 0;

    // Skip refetch if already loaded (non-append, no search)
    if (!append && !search && cities.value.length > 0) {
      return cities.value;
    }

    isLoadingCities.value = true;
    error.value = null;

    try {
      const params = { limit: BATCH_SIZE, offset };
      if (search) params.search = search;

      const response = await axios.get(
        `${api_base_url}/api/cities/`,
        { params, withCredentials: true }
      );

      const data = response.data;
      const results = data.results || data || [];

      if (append) {
        cities.value = [...cities.value, ...results];
      } else {
        cities.value = results;
      }

      citiesTotal.value = data.total || results.length;
      citiesHasMore.value = data.has_more || false;

      return cities.value;
    } catch (err) {
      error.value = err.response?.data || err.message;
      return [];
    } finally {
      isLoadingCities.value = false;
    }
  };

  const collapseCategories = () => {
    if (categories.value.length > BATCH_SIZE) {
      categories.value = categories.value.slice(0, BATCH_SIZE);
      categoriesHasMore.value = categoriesTotal.value > BATCH_SIZE;
    }
  };

  const collapseCities = () => {
    if (cities.value.length > BATCH_SIZE) {
      cities.value = cities.value.slice(0, BATCH_SIZE);
      citiesHasMore.value = citiesTotal.value > BATCH_SIZE;
    }
  };

  async function applyFilters() {
    const heroCat = selectedCategory.value; // from Hero
    const urlCatVal = urlCategory.value; // from URL (?cat)

    let effectiveCategory = 'All';

    if (urlCatVal && heroCat && urlCatVal === heroCat) {
      // ✅ Both match → just use one
      effectiveCategory = urlCatVal;
    } else if (urlCatVal) {
      // ✅ URL takes priority if present
      effectiveCategory = urlCatVal;
    } else if (heroCat && heroCat !== 'All') {
      // ✅ No URL, Hero is a real category
      effectiveCategory = heroCat;
    }

    const filters = {
      category: effectiveCategory === 'All' ? '' : effectiveCategory,
      search: searchQuery.value?.trim() || '',
      categories: selectedCategories.value || [],
      cities: selectedCities.value || [],
      state: selectedState.value || '',
      country: selectedCountry.value || '',
    };

    const noFilters =
      !filters.search &&
      !filters.category &&
      filters.categories.length === 0 &&
      filters.cities.length === 0 &&
      !filters.state &&
      !filters.country;

    if (noFilters) {
      await fetchEquipments();
      filteredEquipments.value = equipments.value;
    } else {
      await fetchFilteredEquipments(filters);
    }
  }

  // Pagination Actions — work for both /equipments/ and /equipments/filter/ URLs
  const fetchNextPage = () => {
    if (nextPageUrl.value) {
      fetchEquipments(nextPageUrl.value);
    }
  };

  const fetchPreviousPage = () => {
    if (previousPageUrl.value) {
      fetchEquipments(previousPageUrl.value);
    }
  };

  const fetchPage = (pageUrl) => {
    if (pageUrl) {
      fetchEquipments(pageUrl);
    }
  };

  const setPageSize = (size) => {
    pageSize.value = size;
    // Clear equipment cache so next fetch uses fresh page_size
    cache.value.equipments = {};
    cache.value.timestamp = {};
    fetchEquipments();
  };

  // Debounced watcher for filters
  const debouncedFetchFiltered = debounce(() => {
    applyFilters();
  }, 300);

  watch(
    [searchQuery, selectedCategory, selectedCategories, selectedCities, selectedState, selectedCountry],
    debouncedFetchFiltered,
    { deep: true }
  );

  // Return state and actions
  return {
    totalPages,
    nextPageUrl,
    previousPageUrl,
    currentPage,
    totalItems,
    pageLinks,
    pageSize,
    truncateText,
    equipments,
    relatedEquipments,
    categories,
    categoriesTotal,
    categoriesHasMore,
    cities,
    citiesTotal,
    citiesHasMore,
    selectedEquipment,
    userEquipments,
    userEditableEquipmentsIds,
    isLoading,
    isLoadingEquipments,
    isLoadingCategories,
    isLoadingCities,
    hasCompletedEquipmentFetch,
    error,
    searchQuery,
    selectedCategory,
    categoryMap,
    selectedCategories,
    selectedCities,
    selectedState,
    selectedCountry,
    filteredEquipments,
    urlCategory,
    applyFilters,
    fetchEquipments,
    fetchRelatedEquipments,
    fetchFilteredEquipments,
    fetchUserEquipments,
    getEquipmentById,
    fetchUserEditableEquipments,
    BATCH_SIZE,
    fetchCategories,
    searchCategories,
    fetchCities,
    collapseCategories,
    collapseCities,
    fetchNextPage,
    fetchPreviousPage,
    fetchPage,
    setPageSize,
  };
});
